/**
 * Demo fetch gate
 *
 * Wraps the global `fetch` once at app start. When the singleton DataService
 * is in demo mode, every request to our custom edge functions
 * (`/functions/v1/make-server-a476273b/*`) is short-circuited with a canned
 * empty response — so the demo client never sees real notifications, chats,
 * presence, billing, leads, RFIs, etc. that other accounts might have.
 *
 * What is *not* blocked:
 *   - Supabase auth (`/auth/v1/*`)            — login still works
 *   - Supabase storage / realtime / REST       — no client UI hits these
 *     directly except for narrow cases that are already gated, and blocking
 *     auth endpoints would prevent the demo from signing in
 *   - Anything outside the make-server prefix  — third-party APIs, Vite HMR,
 *     local assets, etc.
 *
 * Centralising the gate here means we don't have to remember to add a demo
 * check at every call-site (98+ files). One miss without this gate = a leak.
 */

import { DataService } from '../DataService';

const EDGE_FN_MARKER = '/functions/v1/make-server-a476273b/';

// A permissive canned body. JSON parsers pick the shape they expect:
//   - `Array.isArray(data) ? data : []`  → []  (since we return an object)
//   - `data.notifications || []`         → []
//   - `data.chats || []`                 → []
//   - `data.success`                     → true
// Any caller reading a key we don't list falls through to `|| []` / `|| {}`
// patterns, which is what real backends return for "no rows" anyway.
const CANNED_BODY = JSON.stringify({
  success: true,
  ok: true,
  notifications: [],
  data: [],
  items: [],
  chats: [],
  records: [],
  rows: [],
  list: [],
  results: [],
  presence: {},
});

const CANNED_HEADERS = { 'Content-Type': 'application/json' };

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return '';
}

let installed = false;

export function installDemoFetchGate(): void {
  if (installed || typeof globalThis.fetch !== 'function') return;
  installed = true;

  const originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (DataService.isDemoMode()) {
      const url = urlOf(input);
      if (url.includes(EDGE_FN_MARKER)) {
        return new Response(CANNED_BODY, { status: 200, headers: CANNED_HEADERS });
      }
    }
    return originalFetch(input, init);
  };
}
